import { useState, useEffect, useCallback } from 'react';
import { trpc } from '@lib/trpc';

/**
 * Custom hook for ABN validation
 */
export const useABNValidation = (abn: string, excludeId?: string) => {
  const [abnError, setAbnError] = useState<string>('');
  const [isValidating, setIsValidating] = useState(false);

  // Normalize ABN by removing all non-digit characters
  const normalizeABN = useCallback((abn: string): string => {
    return abn.replace(/[^0-9]/g, '');
  }, []);

  // Validate ABN format (11 digits)
  const validateABNFormat = useCallback((abn: string): boolean => {
    const normalized = normalizeABN(abn);
    return normalized.length === 11 && /^\d{11}$/.test(normalized);
  }, [normalizeABN]);

  // Get the tRPC utils for manual queries
  const utils = trpc.useUtils();

  // Debounced validation function
  const validateABN = useCallback(async (abnValue: string) => {
    const trimmedABN = abnValue.trim();
    
    // Clear error for empty ABN (optional field)
    if (!trimmedABN) {
      setAbnError('');
      return true;
    }

    // Check format first
    if (!validateABNFormat(trimmedABN)) {
      setAbnError('ABN must be exactly 11 digits');
      return false;
    }

    // Check uniqueness using manual query with current parameters
    setIsValidating(true);
    try {
      const result = await utils.supplier.checkABNUnique.fetch({
        abn: trimmedABN,
        excludeId
      });
      
      if (result && !result.isUnique) {
        setAbnError('ABN already exists for another supplier');
        return false;
      }
      
      setAbnError('');
      return true;
    } catch (error) {
      setAbnError('Failed to validate ABN uniqueness');
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [validateABNFormat, utils, excludeId]);

  // Debounce ABN validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (abn.trim()) {
        validateABN(abn);
      } else {
        setAbnError('');
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [abn, excludeId, validateABN]); // Include excludeId in dependencies

  return {
    abnError,
    isValidating,
    validateABN,
    normalizeABN,
    validateABNFormat,
  };
};
